import { useEffect, useState } from 'react';

export default function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[2000ms] ease-out ${loaded ? 'scale-100' : 'scale-110'}`}
        style={{
          backgroundImage:
            'url(https://static.readdy.ai/image/7e22dd63abefb8d10cd003bf2bbcd2a0/bb0f2d8a9a976362dcdf9e886ed742a6.png)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent"></div>
      </div>

      {/* Orange glow accent */}
      <div className={`absolute top-1/3 left-1/4 w-80 h-80 bg-[#FF6600]/10 rounded-full blur-3xl pointer-events-none transition-all duration-[2500ms] ${loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-xl">
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-1.5 bg-[#FF6600]/20 border border-[#FF6600]/40 text-[#FF6600] px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest mb-4 md:mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms' }}
            >
              <span className="w-1 h-1 bg-[#FF6600] rounded-full animate-pulse"></span>
              Premium Building Materials
            </div>

            <h1
              className={`text-3xl md:text-4xl lg:text-6xl font-extrabold text-white leading-tight mb-3 md:mb-5 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '400ms' }}
            >
              Build Your
              <span className="block text-[#FF6600]">Dream Space</span>
              With Us
            </h1>

            <p
              className={`text-white/70 text-sm md:text-base leading-relaxed mb-5 md:mb-8 max-w-lg transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '600ms' }}
            >
              From flooring to structural materials, bathroom fixtures to interior finishes — everything you need to create stunning spaces, all in one place.
            </p>

            <div
              className={`flex flex-wrap items-center gap-3 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '800ms' }}
            >
              <button
                onClick={() => scrollToSection('products')}
                className="flex items-center gap-2 bg-[#FF6600] text-white px-5 md:px-7 py-2.5 md:py-3 rounded-full hover:bg-[#E55A00] transition-all duration-300 hover:scale-105 font-semibold text-sm whitespace-nowrap cursor-pointer shadow-lg shadow-[#FF6600]/30"
              >
                Explore Products
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Stats row */}
            <div
              className={`flex flex-wrap gap-5 md:gap-8 mt-8 md:mt-12 pt-5 md:pt-8 border-t border-white/10 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '1000ms' }}
            >
              {[
                { value: '500+', label: 'Products' },
                { value: '5', label: 'Categories' },
                { value: '100%', label: 'Quality' }
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  className={`transition-all duration-600 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${1200 + i * 150}ms` }}
                >
                  <div className="text-xl md:text-2xl font-extrabold text-[#FF6600]">{stat.value}</div>
                  <div className="text-white/50 text-[10px] uppercase tracking-widest mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-6 w-full flex flex-col items-center gap-1.5 text-white/40 animate-bounce transition-all duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ transitionDelay: '1800ms' }}
      >
        <span className="text-[10px] uppercase tracking-widest">Scroll</span>
        <i className="ri-arrow-down-line text-base"></i>
      </div>
    </section>
  );
}
